import { useCallback } from 'react';
import useGetAttachmentUploadUrl from '#/common/components/AttachmentUpload/hooks/useAttachmentUploadUrlMutation';
import useAttachmentCreate from '#/common/components/AttachmentUpload/hooks/useAttachmentCreateMutation';
import useToast from './useToast';

// Hook to upload scanned file attached to the created record
const useUploadScannedFile = () => {
  const { alert } = useToast();
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();

  const uploadScannedFile = useCallback(
    async (table: string, id: string, file: File, tenantId: string) => {
      try {
        if (!tenantId) throw new Error('Missing tenantId for scanned file upload');

        // Get upload URL
        const uploadInfos = await getAttachmentUploadUrls(
          [
            {
              fileName: file.name,
              size: file.size,
              contentType: file.type,
              relatedTable: table,
              relatedItem: id,
              type: 'general',
            },
          ],
          tenantId,
        );

        const uploadInfo = Array.isArray(uploadInfos)
          ? uploadInfos.find((u: any) => u && u.uploadUrl && u.uploadName)
          : undefined;

        if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
          throw new Error('Failed to get upload URL for scanned file');
        }

        // Upload file to S3
        await fetch(uploadInfo.uploadUrl as string, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        // Create attachment record
        await createAttachment({
          fileName: String(uploadInfo.uploadName).split('/').pop() || '',
          size: file.size,
          mimeType: file.type,
          relatedTable: table,
          relatedItem: id,
          type: 'general',
          path: uploadInfo.uploadName as string,
          originalFileName: file.name,
        });
      } catch (error) {
        console.error('Failed to upload scanned file:', error);
        void alert('error', 'Failed to upload scanned image attachment');
      }
    },
    [alert, getAttachmentUploadUrls, createAttachment],
  );

  return uploadScannedFile;
};

export default useUploadScannedFile;
